import { useState } from "react";
import { usePage } from "@inertiajs/react";
import styled, { keyframes } from "styled-components";
import {
  X,
  Utensils,
  MessageCircle,
  ConciergeBell,
  ChevronRight,
} from "lucide-react";
import { siWhatsapp } from "simple-icons";
import { useLandingLanguage } from "../../../contexts/landing-language";
import { z, motion, color } from "../../../styles/tokens";

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Wrapper = styled.div`
  position: fixed;
  bottom: 32px;
  right: 32px;
  /* On the shared scale rather than an ad-hoc 1000, which outranked every popover on the site — it
   * was drawing over the date picker's footer. */
  z-index: ${z.widget};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;

  @media (max-width: 480px) {
    bottom: 20px;
    right: 16px;
  }
`;

const Panel = styled.div`
  background: #ffffff;
  border: 1px solid #e5e1dc;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  width: 280px;
  animation: ${slideUp} 0.25s ease forwards;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 260px;
  }
`;

const PanelHeader = styled.div`
  background: #1a1a1a;
  padding: 18px 20px;
`;

const PanelTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #c9a86c;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const PanelSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 300;
  line-height: 1.5;
`;

const PanelBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServiceLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e5e1dc;
  color: #1a1a1a;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: #faf8f5;
    border-color: #c9a86c;
    color: #1a1a1a;
  }

  svg {
    color: #c9a86c;
    flex-shrink: 0;
  }
`;

const LinkText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LinkTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const LinkDesc = styled.span`
  font-size: 11px;
  color: #8b7355;
  font-weight: 400;
`;

const ToggleButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #1a1a1a;
  border: none;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  &:hover {
    background: #c9a86c;
    transform: scale(1.08);
    box-shadow: 0 12px 32px rgba(201, 168, 108, 0.4);
  }
`;

/* Message-us row.
 *
 * Meta's brand terms forbid recolouring or altering the mark, forbid combining it with other words
 * or images, and require it not be the most prominent thing on the page — so the glyph is drawn
 * unmodified in the official green (#25D366) and everything around it is ours. "Message us on
 * WhatsApp" rather than "WhatsApp us": the same terms say not to use the name as a verb.
 */
const WHATSAPP_GREEN = "#25D366";

const WhatsAppMark = (props) => (
  <svg
    viewBox="0 0 24 24"
    width={props.size || 20}
    height={props.size || 20}
    fill={WHATSAPP_GREEN}
    role="img"
    aria-hidden="true"
    focusable="false"
  >
    <path d={siWhatsapp.path} />
  </svg>
);

const AskRow = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid ${color.border};
  background: ${color.canvas};
  text-decoration: none;
  transition: background ${motion.fast};

  &:hover {
    background: ${color.primarySoft};
  }
`;

const AskText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  strong {
    font-size: 13px;
    font-weight: 600;
    color: ${color.ink};
    line-height: 1.25;
  }

  small {
    font-size: 11px;
    color: ${color.ink500};
  }
`;

const AskChevron = styled.span`
  margin-left: auto;
  color: ${color.ink500};
  flex-shrink: 0;
`;

const GuestWidget = () => {
  const { booking } = usePage().props;
  // Closed by default. Open-on-load put a 280px panel over the hero's primary CTA on every mobile
  // visit — the one button the page exists to get pressed.
  const [open, setOpen] = useState(false);
  const { t } = useLandingLanguage();
  const gw = t.guestWidget;

  return (
    <Wrapper>
      {open && (
        <Panel>
          <PanelHeader>
            <PanelTitle>{gw.title}</PanelTitle>
            <PanelSubtitle>{gw.subtitle}</PanelSubtitle>
          </PanelHeader>
          <PanelBody>
            <ServiceLink href="/hebe/001/hgs">
              <ConciergeBell size={20} />
              <LinkText>
                <LinkTitle>{gw.guestServices}</LinkTitle>
                <LinkDesc>{gw.guestServicesDesc}</LinkDesc>
              </LinkText>
            </ServiceLink>
            <ServiceLink href="/hebe/001/hgs/fnb/">
              <Utensils size={20} />
              <LinkText>
                <LinkTitle>{gw.orderFood}</LinkTitle>
                <LinkDesc>{gw.orderFoodDesc}</LinkDesc>
              </LinkText>
            </ServiceLink>
          </PanelBody>

          {booking?.whatsapp ? (
            <AskRow
              href={`https://wa.me/${booking?.whatsapp || ""}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsAppMark size={20} />
              <AskText>
                <strong>{gw.askTitle}</strong>
                <small>{gw.askDesc}</small>
              </AskText>
              <AskChevron aria-hidden="true">
                <ChevronRight size={16} />
              </AskChevron>
            </AskRow>
          ) : null}
        </Panel>
      )}

      <ToggleButton
        onClick={() => setOpen((v) => !v)}
        aria-label="Guest services"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </ToggleButton>
    </Wrapper>
  );
};

export default GuestWidget;
